module Client.Admin.Index

open Client
open Elmish
open Shared
open Illy.SafeStack.Client
open System
open SafeStack.Client

type AdminButton =
    | NothingHovered
    | AddUser
    | Lock
    | Unlock
    | ResetPassword
    | UserDetails
    | UserApplications
    | News
    | Disable
    | Enable
    | UserInfo
    | UserGuides
    | ExternalLink of name: string

type EditTile =
    | NoTile
    | LockUser of LockUser.Grid.Model
    | UnlockUser of UnlockUser.Grid.Model
    | ManageApplications of ManageApplications.Grid.Model
    | EditUser of EditUser.Grid.Model
    | UserInfo of UserInfo.Model
    | AddUser of AddUser.Model
    | ResetPassword of ResetPassword.Grid.Model
    | ManageNews of ManageNews.Grid.Model
    | DisableUser of DisableUser.Grid.Model
    | EnableUser of EnableUser.Grid.Model
    | UserGuides of UserGuides.Model

type Model =
    {
        Hovered: AdminButton
        AddUserTileAutoFlipped: bool
        Licenses: LicenseCounts option
        EditTile: EditTile
        ExternalLinks: ExternalLink list option
    }

type Msg =
    | ApiError of ApiError
    | LicensesLoaded of LicenseCounts
    | ExternalLinksLoaded of ExternalLink list
    
    | HoverAdminButton of AdminButton
    | FlipAddUserTile 
    | LockUserMsg of LockUser.Grid.Msg
    | UnlockUserMsg of UnlockUser.Grid.Msg
    | ManageApplicationsMsg of ManageApplications.Grid.Msg
    | EditUserMsg of EditUser.Grid.Msg
    | UserInfoMsg of UserInfo.Msg
    | AddUserMsg of AddUser.Msg
    | ResetPasswordMsg of ResetPassword.Grid.Msg
    | ManageNewsMsg of ManageNews.Grid.Msg
    | DisableUserMsg of DisableUser.Grid.Msg
    | EnableUserMsg of EnableUser.Grid.Msg
    | UserGuidesMsg of UserGuides.Msg
    | OpenLockUserTile
    | OpenUnlockUserTile
    | OpenManageApplicationsTile
    | OpenEditUserTile
    | OpenUserInfoTile
    | OpenAddUserOverlay
    | OpenResetPasswordTile
    | OpenManageNewsTile
    | OpenDisableUserTile
    | OpenEnableUserTile
    | EndNotification
    | OpenUserGuidesTile
    | NavigateTo of ExternalLink
    | ExternalUrlLoaded of otp: Guid option * url: string

type Event =
    | AppError of AppError
    | RefreshPublishedNews

let init() =
    {
        Hovered = NothingHovered
        AddUserTileAutoFlipped = false
        Licenses = None
        EditTile = NoTile
        ExternalLinks = None
    }, Cmd.batch [
        Api.secureCall<ILicensingApi,_,_,_> SecuredRoute.Admin (fun api -> api.getCounts) () LicensesLoaded ApiError
        Api.secureCall<IExternalLinksApi,_,_,_> SecuredRoute.Admin (fun api -> api.getExternalLinks) () ExternalLinksLoaded ApiError
        setTimeout 2000 () (fun _ -> FlipAddUserTile) (fun _ -> FlipAddUserTile)
    ]

let dispatchUserInfoMsg model msg =
    match model.EditTile with 
    | EditTile.UserInfo ui ->
        let m, eff = UserInfo.update msg ui
        match eff with
        | SideEffect.Cmd c -> { model with EditTile = UserInfo m }, Cmd.map UserInfoMsg c |> SideEffect.Cmd
        | SideEffect.Event ev ->
            match ev with
            | UserInfo.Event.ApiError err ->
                { model with EditTile = UserInfo m }, err |> AppError.ApiError |> Event.AppError |> SideEffect.Event
            | UserInfo.Event.CloseOverlay ->
                { model with EditTile = NoTile }, SideEffect.none
    | _ -> model, SideEffect.none

let dispatchUserGuidesMsg model msg =
    match model.EditTile with 
    | EditTile.UserGuides ui ->
        let m, eff = UserGuides.update msg ui
        match eff with
        | SideEffect.Cmd c -> { model with EditTile = UserGuides m }, Cmd.map UserGuidesMsg c |> SideEffect.Cmd
        | SideEffect.Event ev ->
            match ev with
            | UserGuides.Event.ApiError err ->
                { model with EditTile = UserGuides m }, err |> AppError.ApiError |> Event.AppError |> SideEffect.Event
            | UserGuides.Event.CloseOverlay ->
                { model with EditTile = NoTile }, SideEffect.none
    | _ -> model, SideEffect.none

let dispatchLockUserMsg model msg =
    match model.EditTile with 
    | EditTile.LockUser lu ->
        let m, eff = LockUser.Grid.update msg lu
        match eff with
        | SideEffect.Cmd c -> {model with EditTile = LockUser m}, Cmd.map LockUserMsg c |> SideEffect.Cmd
        | SideEffect.Event ev ->
            match ev with
            | LockUser.Grid.Event.ApiError err ->
                {model with EditTile = LockUser m}, err |> AppError.ApiError |> Event.AppError |> SideEffect.Event
            | LockUser.Grid.Event.CloseOverlay ->
                {model with EditTile = NoTile}, SideEffect.none
    | _ -> model, SideEffect.none
    
let dispatchUnlockUserMsg model msg =
    match model.EditTile with 
    | EditTile.UnlockUser lu ->
        let m, eff = UnlockUser.Grid.update msg lu
        match eff with
        | SideEffect.Cmd c -> {model with EditTile = UnlockUser m}, Cmd.map UnlockUserMsg c |> SideEffect.Cmd
        | SideEffect.Event ev ->
            match ev with
            | UnlockUser.Grid.Event.ApiError err ->
                {model with EditTile = UnlockUser m}, err |> AppError.ApiError |> Event.AppError |> SideEffect.Event
            | UnlockUser.Grid.Event.CloseOverlay ->
                {model with EditTile = NoTile}, SideEffect.none
    | _ -> model, SideEffect.none
    
let dispatchManageApplicationsMsg model msg =
    match model.EditTile with 
    | EditTile.ManageApplications ma ->
        let m, eff = ManageApplications.Grid.update msg ma
        match eff with
        | SideEffect.Cmd c -> {model with EditTile = ManageApplications m}, Cmd.map ManageApplicationsMsg c |> SideEffect.Cmd
        | SideEffect.Event ev ->
            match ev with
            | ManageApplications.Grid.Event.ApiError err ->
                {model with EditTile = ManageApplications m}, err |> AppError.ApiError |> Event.AppError |> SideEffect.Event
            | ManageApplications.Grid.Event.CloseOverlay ->
                {model with EditTile = NoTile}, SideEffect.none
    | _ -> model, SideEffect.none
      
let dispatchEditUserMsg model msg =
    match model.EditTile with 
    | EditTile.EditUser ma ->
        let m, eff = EditUser.Grid.update msg ma
        match eff with
        | SideEffect.Cmd c -> {model with EditTile = EditUser m}, Cmd.map EditUserMsg c |> SideEffect.Cmd
        | SideEffect.Event ev ->
            match ev with
            | EditUser.Grid.Event.ApiError err ->
                {model with EditTile = EditUser m}, err |> AppError.ApiError |> Event.AppError |> SideEffect.Event
            | EditUser.Grid.Event.CloseOverlay ->
                {model with EditTile = NoTile}, SideEffect.none
    | _ -> model, SideEffect.none
             
let dispatchResetPasswordMsg model msg =
    match model.EditTile with 
    | EditTile.ResetPassword ma ->
        let m, eff = ResetPassword.Grid.update msg ma
        match eff with
        | SideEffect.Cmd c -> {model with EditTile = ResetPassword m}, Cmd.map ResetPasswordMsg c |> SideEffect.Cmd
        | SideEffect.Event ev ->
            match ev with
            | ResetPassword.Grid.Event.ApiError err ->
                {model with EditTile = ResetPassword m}, err |> AppError.ApiError |> Event.AppError |> SideEffect.Event
            | ResetPassword.Grid.Event.CloseOverlay ->
                {model with EditTile = NoTile}, SideEffect.none
    | _ -> model, SideEffect.none
                
let dispatchManageNewsMsg model msg =
    match model.EditTile with 
    | EditTile.ManageNews ma ->
        let m, eff = ManageNews.Grid.update msg ma
        match eff with
        | SideEffect.Cmd c -> {model with EditTile = ManageNews m}, Cmd.map ManageNewsMsg c |> SideEffect.Cmd
        | SideEffect.Event ev ->
            match ev with
            | ManageNews.Grid.Event.AppError err ->
                {model with EditTile = ManageNews m}, err |> Event.AppError |> SideEffect.Event
            | ManageNews.Grid.Event.CloseOverlay ->
                {model with EditTile = NoTile}, SideEffect.none
            | ManageNews.Grid.Event.RefreshPublishedNews ->
                {model with EditTile = ManageNews m}, SideEffect.Event RefreshPublishedNews
    | _ -> model, SideEffect.none
                      
let dispatchDisableUserMsg model msg =
    match model.EditTile with 
    | EditTile.DisableUser ma ->
        let m, eff = DisableUser.Grid.update msg ma
        match eff with
        | SideEffect.Cmd c -> {model with EditTile = DisableUser m}, Cmd.map DisableUserMsg c |> SideEffect.Cmd
        | SideEffect.Event ev ->
            match ev with
            | DisableUser.Grid.Event.ApiError err ->
                {model with EditTile = DisableUser m}, err |> AppError.ApiError |> Event.AppError |> SideEffect.Event
            | DisableUser.Grid.Event.CloseOverlay ->
                {model with EditTile = NoTile}, SideEffect.none
    | _ -> model, SideEffect.none
                               
let dispatchEnableUserMsg model msg =
    match model.EditTile with 
    | EditTile.EnableUser ma ->
        let m, eff = EnableUser.Grid.update msg ma
        match eff with
        | SideEffect.Cmd c -> {model with EditTile = EnableUser m}, Cmd.map EnableUserMsg c |> SideEffect.Cmd
        | SideEffect.Event ev ->
            match ev with
            | EnableUser.Grid.Event.ApiError err ->
                {model with EditTile = EnableUser m}, err |> AppError.ApiError |> Event.AppError |> SideEffect.Event
            | EnableUser.Grid.Event.CloseOverlay ->
                {model with EditTile = NoTile}, SideEffect.none
    | _ -> model, SideEffect.none
         
let dispatchAddUserMsg model msg =
    match model.EditTile with 
    | EditTile.AddUser ma ->
        let m, eff = AddUser.update msg ma
        match eff with
        | SideEffect.Cmd c -> {model with EditTile = AddUser m}, Cmd.map AddUserMsg c |> SideEffect.Cmd
        | SideEffect.Event ev ->
            match ev with
            | AddUser.Event.ApiError err ->
                {model with EditTile = AddUser m}, err |> AppError.ApiError |> Event.AppError |> SideEffect.Event
            | AddUser.Event.CloseOverlay ->
                {model with EditTile = NoTile}, SideEffect.none
            | AddUser.Event.SaveSuccessful notification ->
                {model with
                    Licenses = None
                    EditTile = NoTile
                },
                Cmd.batch [
                    Api.secureCall<ILicensingApi,_,_,_> SecuredRoute.Admin (fun api -> api.getCounts) () LicensesLoaded ApiError
                    Notification.success notification EndNotification
                ] |> SideEffect.Cmd
    | _ -> model, SideEffect.none
    
let update msg model =
    match msg with
    | FlipAddUserTile ->
        let m = 
            match model.Hovered with
            | AdminButton.NothingHovered ->
                {model with
                    AddUserTileAutoFlipped = true
                    Hovered = AdminButton.AddUser
                }
            | AdminButton.AddUser when model.AddUserTileAutoFlipped ->
                {model with
                    AddUserTileAutoFlipped = false
                    Hovered = AdminButton.NothingHovered
                }
            | _ -> model
        m, setTimeout 5000 () (fun _ -> FlipAddUserTile) (fun _ -> FlipAddUserTile) |> SideEffect.Cmd
    | HoverAdminButton btn ->
        {model with
            Hovered = btn
            AddUserTileAutoFlipped = false
        }, SideEffect.none
    | ApiError err -> model, err |> AppError.ApiError |> Event.AppError |> SideEffect.Event
    | ExternalLinksLoaded links ->
        {model with ExternalLinks = Some links}, SideEffect.none
    | LicensesLoaded licenses ->
        {model with Licenses = Some licenses}, SideEffect.none
    | OpenLockUserTile ->
        let m, c = LockUser.Grid.init ()
        {model with EditTile = LockUser m}, Cmd.map LockUserMsg c |> SideEffect.Cmd
    | LockUserMsg luMsg -> dispatchLockUserMsg model luMsg
    | OpenUnlockUserTile ->
        let m, c = UnlockUser.Grid.init ()
        {model with EditTile = UnlockUser m}, Cmd.map UnlockUserMsg c |> SideEffect.Cmd
    | UnlockUserMsg luMsg -> dispatchUnlockUserMsg model luMsg
    | OpenManageApplicationsTile ->
        let m, c = ManageApplications.Grid.init ()
        {model with EditTile = ManageApplications m}, Cmd.map ManageApplicationsMsg c |> SideEffect.Cmd
    | ManageApplicationsMsg maMsg -> dispatchManageApplicationsMsg model maMsg
    | OpenEditUserTile ->
        let m, c = EditUser.Grid.init ()
        {model with EditTile = EditUser m}, Cmd.map EditUserMsg c |> SideEffect.Cmd
    | OpenResetPasswordTile ->
        let m, c = ResetPassword.Grid.init ()
        {model with EditTile = ResetPassword m}, Cmd.map ResetPasswordMsg c |> SideEffect.Cmd
    | ResetPasswordMsg maMsg -> dispatchResetPasswordMsg model maMsg
    | OpenManageNewsTile ->
        let m, c = ManageNews.Grid.init ()
        {model with EditTile = ManageNews m}, Cmd.map ManageNewsMsg c |> SideEffect.Cmd
    | ManageNewsMsg maMsg -> dispatchManageNewsMsg model maMsg
    | OpenDisableUserTile ->
        let m, c = DisableUser.Grid.init ()
        {model with EditTile = DisableUser m}, Cmd.map DisableUserMsg c |> SideEffect.Cmd
    | DisableUserMsg maMsg -> dispatchDisableUserMsg model maMsg
    | OpenEnableUserTile ->
        let m, c = EnableUser.Grid.init ()
        {model with EditTile = EnableUser m}, Cmd.map EnableUserMsg c |> SideEffect.Cmd
    | EnableUserMsg maMsg -> dispatchEnableUserMsg model maMsg
    | OpenUserInfoTile ->
        let m, c = UserInfo.init ()
        { model with EditTile = UserInfo m }, Cmd.map UserInfoMsg c |> SideEffect.Cmd
    | EditUserMsg maMsg -> dispatchEditUserMsg model maMsg
    | UserInfoMsg maMsg -> dispatchUserInfoMsg model maMsg
    | OpenAddUserOverlay ->
        let m, c = AddUser.init ()
        {model with EditTile = AddUser m}, Cmd.map AddUserMsg c |> SideEffect.Cmd
    | AddUserMsg auMsg -> dispatchAddUserMsg model auMsg
    | EndNotification -> model, SideEffect.none
    | UserGuidesMsg umsg -> dispatchUserGuidesMsg model umsg
    | OpenUserGuidesTile ->
        let m, c = UserGuides.init ()
        {model with EditTile = UserGuides m}, Cmd.map UserGuidesMsg c |> SideEffect.Cmd
    | NavigateTo lnk -> 
        model, Api.secureCall<IExternalLinksApi,_,_,_> SecuredRoute.Admin (fun api -> api.getLandingUrl) lnk.Id ExternalUrlLoaded ApiError |> SideEffect.Cmd
    | ExternalUrlLoaded(otp, url) ->
        let url = sprintf "%s%s" url (otp |> Option.map (sprintf "/%O") |> Option.defaultValue "")
        Browser.Dom.window.``open``(url=url, target="_blank") |> ignore
        model, SideEffect.none

open Fable.React
open Fable.React.Props

let renderEditTile dispatch =
    function
    | EditTile.NoTile -> []
    | EditTile.LockUser lu -> LockUser.Grid.view lu (LockUserMsg >> dispatch)
    | EditTile.UnlockUser lu -> UnlockUser.Grid.view lu (UnlockUserMsg >> dispatch)
    | EditTile.ManageApplications ma -> ManageApplications.Grid.view ma (ManageApplicationsMsg >> dispatch)
    | EditTile.EditUser ma -> EditUser.Grid.view ma (EditUserMsg >> dispatch)
    | EditTile.UserInfo ui -> UserInfo.view ui (UserInfoMsg >> dispatch)
    | EditTile.AddUser au -> AddUser.view au (AddUserMsg >> dispatch)
    | EditTile.ResetPassword au -> ResetPassword.Grid.view au (ResetPasswordMsg >> dispatch)
    | EditTile.ManageNews au -> ManageNews.Grid.view au (ManageNewsMsg >> dispatch)
    | EditTile.DisableUser au -> DisableUser.Grid.view au (DisableUserMsg >> dispatch)
    | EditTile.EnableUser au -> EnableUser.Grid.view au (EnableUserMsg >> dispatch)
    | EditTile.UserGuides g -> UserGuides.view g (UserGuidesMsg >> dispatch)

let renderAdminTile id colour title image descriptionContent onClick dispatch identifier currentlyHovered =
    div [
        Id id
        ClassName ("live-tile nohover flip ha " + colour + if identifier = currentlyHovered then " flipped" else "")
        OnClick (fun _ -> dispatch onClick)
        OnMouseEnter (fun _ -> dispatch (HoverAdminButton identifier))
        OnMouseLeave (fun _ -> dispatch (HoverAdminButton NothingHovered))
    ] [
        span [ ClassName "tile-title" ] [ str title ]
        div [ ClassName ("image flip-front ha " + image) ] []
        div [ ClassName ("flip-back ha") ] [
            yield! descriptionContent
        ]
    ]

let renderStandardAdminTile id colour title image description =
    renderAdminTile id colour title image [ p [] [ str description ] ]

let view model dispatch =
    div [ 
        ClassName "home_theme admin_theme" 
        Style [ ZIndex 500 ]
    ] [
        yield div [
            Id "adminBar"
        ] [
            h1 [] [ str "Administrative Options" ]
            div [ ClassName "blue clearfix" ] [
                renderAdminTile "tile-add" "license" "Add User" "add"
                    [
                        yield span [] [ str "Licenses: "]
                        yield br []
                        match model.Licenses with
                        | None -> yield str "loading..."
                        | Some ls ->
                            yield span [] [ str (ls.Used.ToString()) ]
                            yield str " of "
                            yield span [] [ str (ls.Total.ToString()) ]

                    ]
                    OpenAddUserOverlay dispatch AdminButton.AddUser model.Hovered
                renderStandardAdminTile "tile-lock" "mango" "Lock User" "lock" "Lock a user, preventing them from logging into the system until unlocked. " OpenLockUserTile dispatch Lock model.Hovered
                renderStandardAdminTile "tile-unlock" "emerald" "Unlock User" "unlock" "Unlock a user, allowing them to log in to the system if they were locked." OpenUnlockUserTile dispatch Unlock model.Hovered
                renderStandardAdminTile "tile-password" "cobalt" "Reset Password" "password" "Reset a user's password - they will then be emailed with a reset link." OpenResetPasswordTile dispatch AdminButton.ResetPassword model.Hovered
                renderStandardAdminTile "tile-users" "" "User Details and Tags" "users" "Update user details, or organise your users into different groups using tags." OpenEditUserTile dispatch UserDetails model.Hovered
                renderStandardAdminTile "tile-application" "indigo" "User Applications" "applications" "Add, change or remove user access to applications." OpenManageApplicationsTile dispatch UserApplications model.Hovered
                renderStandardAdminTile "tile-news" "magenta" "Manage News" "news" "Add, remove & publish news stories shown to users on their home page." OpenManageNewsTile dispatch News model.Hovered
                renderStandardAdminTile "tile-deactivate" "crimson" "Disable User" "deactivate" "Remove a user from the system, freeing a licence." OpenDisableUserTile dispatch Disable model.Hovered
                renderStandardAdminTile "tile-reactivate" "emerald" "Reactivate User" "reactivate" "Restore a deactivated user, consuming a licence." OpenEnableUserTile dispatch Enable model.Hovered
                renderStandardAdminTile "tile-info" "steel" "User Info" "info" "Find information about user accounts on the system." OpenUserInfoTile dispatch AdminButton.UserInfo model.Hovered
                renderStandardAdminTile "tile-guides" "magenta" "User Guides" "questionmark" "Download all user guides for CarePath and User Driven Reporting." OpenUserGuidesTile dispatch AdminButton.UserGuides model.Hovered
            ]
            div [ ClassName "externalLinks" ] [
                div [ ClassName "clearfix pluginLink" ] [
                    yield h1 [] [ str "CarePath" ]
                    for link in model.ExternalLinks |> Option.defaultValue [] do
                        yield renderStandardAdminTile "" "orange" link.Name "link" link.Description (NavigateTo link) dispatch (ExternalLink link.Name) model.Hovered
                ]
            ]
        ]
        yield! renderEditTile dispatch model.EditTile
    ]
